import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "react-router-dom";
import { useAuth } from "@/client/hooks/use-auth";
import { api } from "@/client/lib/api";
import { useAgentContext } from "./use-agent-context";

interface RecordSummary {
  id: number;
  insured: string | null;
  address: string | null;
  reinstatementTotal: number | null;
  isActive: boolean;
}

function RouteCollector(): null {
  const location = useLocation();
  useAgentContext({
    key: "route",
    label: "Route",
    value: { pathname: location.pathname, search: location.search },
    priority: 100,
  });
  return null;
}

function UserCollector(): null {
  const { user } = useAuth();
  useAgentContext({
    key: "user",
    label: "User",
    value: user ? { id: user.id, name: user.name, email: user.email, role: user.role } : null,
    priority: 90,
  });
  return null;
}

function RecordCollector(): null {
  const params = useParams<{ id: string }>();
  const location = useLocation();

  const isRecordRoute = Boolean(location.pathname.match(/^\/insurance\/\d+/));
  const recordId = isRecordRoute && params.id ? Number(params.id) : null;

  const { data: record } = useQuery<RecordSummary | null>({
    queryKey: ["insurance-summary", recordId],
    queryFn: async () => {
      if (!recordId) return null;
      const result = await api.get<RecordSummary>(`/api/insurance/${recordId}/summary`);
      return result;
    },
    enabled: recordId !== null,
    staleTime: 1000 * 60 * 2,
  });

  useAgentContext({
    key: "active_record",
    label: "Active record",
    value: record ?? null,
    priority: 95,
  });

  return null;
}

function VisibleQueriesCollector(): null {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryCache().getAll();

  const summary = cache
    .filter((q) => q.state.status === "success")
    .map((q) => ({
      key: q.queryKey,
      size: Array.isArray(q.state.data) ? (q.state.data as unknown[]).length : 1,
    }))
    .slice(0, 20);

  useAgentContext({
    key: "visibleQueries",
    label: "Loaded data",
    value: summary,
    priority: 30,
  });

  return null;
}

/**
 * Mounts inside AgentProvider. Registers live page state into the context
 * registry. Renders nothing.
 */
export function AutoContextCollector(): null {
  return (
    <>
      <RouteCollector />
      <UserCollector />
      <RecordCollector />
      <VisibleQueriesCollector />
    </>
  ) as unknown as null;
}
